/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { BrandingUiInfoKeys } from '../../../core/entities';
import { ErrorTypes } from '../../../core/error';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../../core/network/NetworkManager';
import type { TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { BrandingUiUtility } from '../../../core/utils';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController, showGuideScreen } from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import { OverlayTypes } from '../OverlayTypes';

const GLOBAL_MOUSE_TRAP = 'noNetworkOverlayGalleryScreen';
const TAG: string = 'GalleryScreenNoNetworkOverlay';

export const showNetworkOverlayGalleryScreen = () => (dispatch: Function, getState: Function) => {
    const { isActive: isAntiBurnActive } = getState().antiburnOsd;
    if (isAntiBurnActive) return;

    const services = new ServicesClientImpl();
    const replaceableValues = {
        PHONE_CUSTOMER_SUPPORT_NUMBER: services.getBrandingUiInfoForKey(
            BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
        ),
        CUSTOMER_SUPPORT_NAME: services.getBrandingUiInfoForKey(
            BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME,
        ),
        ERROR_CODE: ErrorTypes.NO_NETWORK_OVERLAY_ERROR_CODE,
    };
    const overlayDialogProps: IOverlayDialog = {
        description: BrandingUiUtility.formatDescription(
            Locale.getString(LocaleKeys.GALLERY_SCREEN_OVERLAY_ERROR_MESSAGE),
            replaceableValues,
        ),
        hasDimmer: true,
        hasSeperator: false,
        messageCode: ErrorTypes.NO_NETWORK_OVERLAY_ERROR_CODE,
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: [
            { text: Locale.getString(LocaleKeys.OVERLAY_OPTION_TRY_AGAIN_TEXT) },
            { text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT) },
        ],
        overlayType: OverlayTypes.NO_NETWORK,
        title: Locale.getString(LocaleKeys.GALLERY_SCREEN_NO_NETWORK_OVERLAY_TITLE),
    };
    dispatch(
        setScreenTaskController(ScreenTaskControllerTypes.NO_NETWORK_OVERLAY_TASKS_GALLERY_SCREEN),
    );
    dispatch(showOverlayDialog(overlayDialogProps));
};

export const onKeyOkNoNetworkOverlayGalleryScreen =
    () => (dispatch: Function, getState: TGetState) => {
        Diagnostics.error(TAG, 'User selected an option on No Network Overlay in Gallery Screen');

        const { verticalPosition } = getState().overlayHighlight;
        const { uiProperties } = getState().overlayDialog;
        const { options } = uiProperties;
        const selectedOption = options[verticalPosition];

        if (selectedOption.text === Locale.getString(LocaleKeys.OVERLAY_OPTION_TRY_AGAIN_TEXT)) {
            if (NetworkManager.isNetworkDisconnected()) {
                dispatch(hideOverlayDialog());
                dispatch(showNetworkWhisperGalleryScreen());
            } else {
                // TODO : Here we should ideally go back to the next screen of selected tile.
            }
        } else {
            dispatch(hideOverlayDialog());
            dispatch(showGuideScreen());
        }
    };

export const onKeyBackNoNetworkOverlayGalleryScreen = () => (dispatch: Function) => {
    Diagnostics.debug(TAG, 'User selected back button on No Network Overlay in Gallery Screen');
    dispatch(hideOverlayDialog());
};

export const onMouseClickNoNetworkOverlayGalleryScreen =
    () => (dispatch: Function, getState: Function) => {
        // TODO: Implement the logic for handling mouse over event in gallery
    };

export const showNetworkWhisperGalleryScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) return;
    const message = `${Locale.getString(LocaleKeys.SOMETHING_WENT_WRONG_WHISPER_DESCRIPTION)} ${Locale.getString(LocaleKeys.PRESS_OK_FOR_MORE_INFORMATION)} (${ErrorTypes.NO_NETWORK_OVERLAY_ERROR_CODE})`;
    dispatch(
        showWhisper({
            message,
            iconType: WhisperTypes.INFO,
        }),
    );
};

export const showErrorOverlayGalleryScreen =
    (errorCode: string) => (dispatch: Function, getState: Function) => {
        const { isActive: isAntiBurnActive } = getState().antiburnOsd;
        if (isAntiBurnActive) return;

        const replaceableValues = { ERROR_CODE: errorCode };
        const overlayDialogProps: IOverlayDialog = {
            description: BrandingUiUtility.formatDescription(
                Locale.getString(LocaleKeys.VOD_BROWSE_ERROR_OVERLAY_DESCRIPTION),
                replaceableValues,
            ),
            hasDimmer: true,
            hasSeperator: false,
            messageCode: errorCode,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT) }],
            overlayType: OverlayTypes.ERROR,
            title: Locale.getString(LocaleKeys.VOD_BROWSE_ERROR_OVERLAY_TITLE),
        };
        dispatch(
            setScreenTaskController(ScreenTaskControllerTypes.ERROR_OVERLAY_TASKS_GALLERY_SCREEN),
        );
        dispatch(showOverlayDialog(overlayDialogProps));
    };
